// 覚醒王の後ろ跳びの FX = 案B「着地で床を掴む爪の粉」(2026-10-05 ユーザー採用)。
//   出所 = docs/ledger/awoken-king.md 2026-10-05「牙の試し（14・15番×3案）と後ろ跳びのFXの回答」・
//   場面の絵 C:\dev\LUNAIRE sinige\.ai-team\checks\awoken-hop-fx-1005\img\scene_B_1.jpg / scene_B_2.jpg・
//   方向の案 docs/research/2026-10-05-awoken-back-hop-fx-references.md §2 案B。
//   踏み切り = 後脚の爪先から細かい粉の筋が主人公側へ低く (7本・250ms)。
//   着地 = 後脚→前脚 (40ms 遅れ) の金の爪の先から暗い小石と明るい粉が短く弾け、薄い煙が少し湧く (小石5・粉の筋8・煙4・500ms)。
//   跡 = 爪の位置から床に短い引っかき跡 (1脚3本)。着地の滑り (awoken_spacing_choice.ts awokenHopSlidePx) の分だけ伸び、1.5秒で消える。
//   爪の位置 = 絵の原画 px (2026-10-05 Sonnet 測定・確認画像で目視)。描いた絵の置き場所 (placement) から毎フレーム写す。
//   材料は騎士の床を削る粒 (knight_floor_scrape.ts) と回転の土煙 (awoken_spin_fx.ts・明るい灰をぼかした柔らかい煙) に揃える。
import { arenaBoundsFor } from './balance'
import { SPACING } from './awoken_spacing_choice'
import type { Boss } from './types'
import { projectBossSpriteSourcePoint, type BossSpritePlacement } from './sprites'
import { blurPx } from './render_scale'

type Pt = readonly [number, number]
/** 脚ごとの爪の先 (原画 px)。[0] = 奥の脚・[1] = 手前の脚。爪は左 (主人公側) から順。 */
export const AWOKEN_HOP_CLAWS: Record<string, { front: Pt[][]; rear: Pt[][] }> = {
  back_hop_crouch_1001: {
    front: [[[60, 775], [101, 808]], [[253, 826], [285, 831], [348, 836]]],
    rear: [[[1183, 799], [1212, 805]], [[1442, 825], [1487, 831], [1549, 836]]],
  },
  back_hop_land_1001: {
    front: [[[66, 828], [100, 840]], [[364, 844], [399, 853], [460, 857]]],
    rear: [[[951, 828], [986, 835]], [[1423, 845], [1472, 852], [1537, 853]]],
  },
}
export const AWOKEN_HOP_FRONT_DELAY_MS = 40
export const AWOKEN_HOP_SCRATCH_LIFE_MS = 1500
/** 滑らない時の跡の長さ (爪が床に食い込んだ分・爪の前へ)。 */
export const AWOKEN_HOP_DIG_PX = 4
const GRIT_G = 1300
const PEBBLE_G = 1700
const DUST_RGB = '156,146,156'

interface Grit { x: number; y: number; floorY: number; vx: number; vy: number; born: number; life: number; size: number; a: number; tone: number }
interface Pebble { x: number; y: number; floorY: number; vx: number; vy: number; born: number; life: number; r: number; spin: number; rot: number; verts: number[] }
interface Puff { x: number; y: number; vx: number; vy: number; born: number; life: number; size: number; seed: number }
interface Scratch { y: number; x0: number; x1: number; born: number; w: number; a: number }
interface PawMarks { lines: Scratch[]; tipIdx: number[]; paw: number; leg: 'front' | 'rear' }

const hash01 = (n: number): number => { const s = Math.sin(n * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s) }

export class AwokenHopFx {
  grits: Grit[] = []
  pebbles: Pebble[] = []
  puffs: Puff[] = []
  scratches: Scratch[] = []
  private hopId = ''
  private takeoffDone = false
  private rearLanded = false
  private frontLanded = false
  private landAtMs = -1
  private rearCrouch: { x: number; y: number }[] = []
  private marks: PawMarks[] = []
  private lastNow = -1
  private lastRoom = -1
  private lastSlideX = NaN
  constructor(private readonly rng: () => number = Math.random) {}
  private rand(a: number, b: number): number { return a + this.rng() * (b - a) }

  clear(): void {
    this.grits = []; this.pebbles = []; this.puffs = []; this.scratches = []
    this.resetHop('')
    this.lastNow = -1; this.lastRoom = -1
  }
  private resetHop(id: string): void {
    this.hopId = id; this.takeoffDone = false; this.rearLanded = false; this.frontLanded = false
    this.landAtMs = -1; this.rearCrouch = []; this.marks = []; this.lastSlideX = NaN
  }

  /** 粉の筋: 同じ向きへ速さ違いの粒を並べる = 床すれすれの1本の筋に見える。 */
  emitStreak(x: number, y: number, dir: number, now: number, power: number, life: number): void {
    const ang = this.rand(0.04, 0.42)
    const speed = this.rand(320, 620) * (0.55 + 0.45 * power)
    const n = Math.round(this.rand(5, 9))
    for (let i = 0; i < n; i++) {
      const k = Math.pow(this.rng(), 0.7)
      const v = speed * (0.4 + 0.65 * k)
      const a = ang + this.rand(-0.04, 0.04)
      this.grits.push({ x: x + this.rand(-1.5, 1.5), y: y - this.rand(0, 1.5), floorY: y + 0.5, vx: dir * Math.cos(a) * v, vy: -Math.sin(a) * v,
        born: now, life: life * this.rand(0.7, 1.15), size: this.rand(0.5, 1.25) * (1.2 - 0.45 * k), a: this.rand(0.4, 0.8), tone: this.rng() })
    }
  }
  emitPebble(x: number, y: number, dir: number, now: number): void {
    const ang = this.rand(0.45, 1.15)
    const v = this.rand(150, 300)
    const verts: number[] = []
    for (let i = 0, nv = Math.round(this.rand(5, 7)); i < nv; i++) verts.push(this.rand(0.62, 1))
    this.pebbles.push({ x: x + this.rand(-2, 2), y: y - 1.5, floorY: y + 0.5, vx: dir * Math.cos(ang) * v, vy: -Math.sin(ang) * v, born: now,
      life: this.rand(420, 560), r: this.rand(1.1, 2.1), spin: this.rand(-14, 14), rot: this.rand(0, 6.3), verts })
  }
  emitPuff(x: number, y: number, dir: number, now: number, size: number): void {
    this.puffs.push({ x: x + this.rand(-2, 2), y: y - 1, vx: dir * this.rand(25, 60), vy: -this.rand(8, 22), born: now,
      life: this.rand(420, 520), size: size * this.rand(0.85, 1.15), seed: this.rng() * 1000 })
  }

  /** 着地の一撃 (1脚)。near = 手前の脚 (量を多く)。dir = 跳んだ向き (滑る向き)。 */
  private emitLand(tips: { x: number; y: number }[], dir: number, now: number, near: boolean, pebbles: number, streaks: number): void {
    const lead = tips[0]!
    for (let i = 0; i < pebbles; i++) this.emitPebble(lead.x, lead.y, this.rng() < 0.7 ? dir : -dir, now)
    for (let i = 0; i < streaks; i++) {
      const t = tips[i % tips.length]!
      this.emitStreak(t.x, t.y, this.rng() < 0.65 ? dir : -dir, now, near ? 0.7 : 0.45, 260)
    }
    this.emitPuff(lead.x + dir * 3, lead.y, dir, now, near ? 7 : 5)
  }

  private startMarks(leg: 'front' | 'rear', tipsByPaw: { x: number; y: number }[][], dir: number, now: number): void {
    tipsByPaw.forEach((tips, paw) => {
      // 1脚3本。奥の脚は見える爪が2本なので、間にもう1本足す。
      const lineTips = tips.length >= 3 ? tips.slice(0, 3)
        : [tips[0]!, { x: (tips[0]!.x + tips[1]!.x) / 2, y: (tips[0]!.y + tips[1]!.y) / 2 }, tips[1]!]
      const lines = lineTips.map((t, i) => {
        // 爪の前 (主人公側 = 滑りと逆) へ食い込んだ分だけ最初から伸ばす。
        // 3本は床の奥行きで少しずつずらし (上下 1.3px)、食い込みの長さも爪ごとに不揃いにする (揃った棒に見せない)。
        const h = (n: number) => hash01(now * 0.37 + i * 11 + paw * 7 + n)
        const s: Scratch = { y: lineTips[1]!.y + (i - 1) * 1.3 + 0.3, x0: t.x - dir * AWOKEN_HOP_DIG_PX * (0.6 + 0.8 * h(1)), x1: t.x, born: now,
          w: (paw === 1 ? 1.2 : 0.85) * (0.8 + 0.4 * h(2)), a: (paw === 1 ? 0.85 : 0.55) * (0.75 + 0.4 * h(3)) }
        this.scratches.push(s)
        return s
      })
      this.marks.push({ lines, tipIdx: lineTips.map((_, i) => i), paw, leg })
    })
  }

  sync(boss: Boss, now: number, placement: BossSpritePlacement | null): void {
    if (boss.id !== 'crowned-king-awoken' || boss.hp <= 0) { this.clear(); return }
    if ((this.lastNow >= 0 && now < this.lastNow) || (this.lastRoom >= 0 && boss.executesTaken !== this.lastRoom)) this.clear()
    this.lastNow = now
    this.lastRoom = boss.executesTaken
    const s = boss.spacing
    const hopping = boss.state === 'idle' && s?.action === 'hop' && !s.done
    if (!hopping || !s) { if (this.hopId) this.resetHop(''); return }
    const id = `${s.fromX.toFixed(2)}:${s.toX.toFixed(2)}`
    if (id !== this.hopId || s.elapsedMs < SPACING.hop.crouchMs * 0.5 && this.takeoffDone) this.resetHop(id)
    if (!placement) return
    const claws = AWOKEN_HOP_CLAWS[placement.imageKey]
    const project = (pts: Pt[]) => pts.map(p => projectBossSpriteSourcePoint(placement, p)).filter((p): p is { x: number; y: number } => !!p)
    const dir = s.dir
    const walls = arenaBoundsFor(boss.id)
    const inside = (p: { x: number }) => p.x >= walls.leftWall && p.x <= walls.rightWall

    if (placement.imageKey === 'back_hop_crouch_1001' && claws) {
      this.rearCrouch = claws.rear.map(paw => project(paw)[paw.length - 1]!).filter(Boolean)
      return
    }
    if (placement.imageKey === 'back_hop_air_1001') {
      if (!this.takeoffDone && this.rearCrouch.length) {
        // 踏み切り: 後脚の爪先 (屈みの絵の最後の位置) から、跳ぶ向きと逆 (主人公側) へ。手前4本・奥3本。
        this.rearCrouch.forEach((p, paw) => {
          if (!inside(p)) return
          for (let i = 0; i < (paw === 1 ? 4 : 3); i++) this.emitStreak(p.x, p.y, -dir, now, paw === 1 ? 1 : 0.6, 250)
        })
      }
      this.takeoffDone = true
      return
    }
    if (placement.imageKey !== 'back_hop_land_1001' || !claws) return
    this.takeoffDone = true
    const rear = claws.rear.map(project)
    const front = claws.front.map(project)
    if (!this.rearLanded) {
      this.rearLanded = true
      this.landAtMs = now
      rear.forEach((tips, paw) => { if (tips.length && inside(tips[0]!)) this.emitLand(tips, dir, now, paw === 1, paw === 1 ? 2 : 1, paw === 1 ? 3 : 1) })
      this.startMarks('rear', rear, dir, now)
    }
    if (!this.frontLanded && now - this.landAtMs >= AWOKEN_HOP_FRONT_DELAY_MS) {
      this.frontLanded = true
      front.forEach((tips, paw) => { if (tips.length && inside(tips[0]!)) this.emitLand(tips, dir, now, paw === 1, paw === 1 ? 2 : 0, paw === 1 ? 3 : 1) })
      this.startMarks('front', front, dir, now)
    }
    // 滑り: 跡の先を今の爪の位置まで伸ばし、動いた分だけ爪先から細かい粉を少し。
    for (const m of this.marks) {
      const tips = (m.leg === 'rear' ? rear : front)[m.paw]
      if (!tips || tips.length < 2) continue
      const lineTips = tips.length >= 3 ? tips : [tips[0]!, { x: (tips[0]!.x + tips[1]!.x) / 2, y: tips[0]!.y }, tips[1]!]
      m.lines.forEach((ln, i) => {
        const tx = lineTips[m.tipIdx[i]!]!.x
        if ((tx - ln.x1) * dir > 0) ln.x1 = tx
      })
    }
    const lead = rear[1]?.[0]
    if (lead) {
      const moved = Number.isNaN(this.lastSlideX) ? 0 : Math.abs(lead.x - this.lastSlideX)
      if (moved > 0.6 && this.rng() < Math.min(1, moved / 3)) {
        for (const tips of [rear[1], front[1]]) if (tips?.[0]) this.emitStreak(tips[0].x, tips[0].y, dir, now, 0.35, 200)
      }
      this.lastSlideX = lead.x
    }
  }

  /** 床の層 (体より先): 引っかき跡。 */
  drawFloor(ctx: CanvasRenderingContext2D, now: number): void {
    this.scratches = this.scratches.filter(s => now - s.born < AWOKEN_HOP_SCRATCH_LIFE_MS)
    if (!this.scratches.length) return
    ctx.save()
    for (const s of this.scratches) {
      const age = now - s.born
      // 0.35秒までははっきり、あとは 1.5秒でゆっくり消える。
      const fade = age < 350 ? 1 : 1 - ((age - 350) / (AWOKEN_HOP_SCRATCH_LIFE_MS - 350)) ** 0.8
      const len = Math.abs(s.x1 - s.x0)
      if (len < 0.5 || fade <= 0) continue
      // 溝の影 (下に寄せた暗い細線) と、削れた明るい筋。どちらも爪の側 (x1) が太く濃く、始まりの側へ細く消える (くさび形)。
      const wedge = (y: number, w: number, rgb: string, a: number) => {
        const g = ctx.createLinearGradient(s.x0, 0, s.x1, 0)
        g.addColorStop(0, `rgba(${rgb},0)`)
        g.addColorStop(Math.min(0.6, 4 / len), `rgba(${rgb},${(a * 0.7).toFixed(3)})`)
        g.addColorStop(1, `rgba(${rgb},${a.toFixed(3)})`)
        ctx.fillStyle = g
        ctx.beginPath()
        ctx.moveTo(s.x0, y)
        ctx.lineTo(s.x1, y - w / 2)
        ctx.lineTo(s.x1, y + w / 2)
        ctx.closePath()
        ctx.fill()
      }
      wedge(s.y + 0.7, s.w * 0.9, '40,36,50', s.a * 0.5 * fade)
      wedge(s.y, s.w, '222,218,232', s.a * fade)
    }
    ctx.restore()
  }

  /** 体の後: 煙・粉の筋・小石。 */
  draw(ctx: CanvasRenderingContext2D, now: number, bossId: string): void {
    const walls = arenaBoundsFor(bossId)
    this.grits = this.grits.filter(g => now - g.born < g.life)
    this.pebbles = this.pebbles.filter(p => now - p.born < p.life)
    this.puffs = this.puffs.filter(p => now - p.born < p.life)
    if (this.puffs.length) {
      // 柔らかい煙: かけらを3つずつ重ね、ぼかして輪郭を消す (回転の土煙と同じ明るい灰)。
      ctx.save()
      ctx.filter = `blur(${blurPx(2.2)}px)`
      for (const p of this.puffs) {
        const t = (now - p.born) / 1000
        const u = (now - p.born) / p.life
        const drag = (1 - Math.exp(-3 * t)) / 3
        const cx = p.x + p.vx * drag, cy = p.y + p.vy * drag
        const al = 0.5 * (1 - u) ** 1.4 * Math.min(1, u / 0.12)
        for (let j = 0; j < 3; j++) {
          const h = (n: number) => hash01(p.seed + j * 17 + n)
          const r = p.size * (0.45 + 0.35 * h(1)) * (0.6 + 1.1 * u)
          ctx.fillStyle = `rgba(${DUST_RGB},${(al * (0.6 + 0.4 * h(2))).toFixed(3)})`
          ctx.beginPath()
          ctx.ellipse(cx + (h(3) - 0.5) * p.size * 1.4, cy - h(4) * p.size * 0.5 * (0.5 + u), r, r * 0.62, 0, 0, Math.PI * 2)
          ctx.fill()
        }
      }
      ctx.restore()
    }
    if (this.grits.length) {
      ctx.save()
      ctx.lineCap = 'round'
      for (const g of this.grits) {
        const t = (now - g.born) / 1000
        const pos = (tt: number) => {
          const drag = (1 - Math.exp(-2.6 * tt)) / 2.6
          return { x: g.x + g.vx * drag, y: Math.min(g.floorY - 0.4, g.y + g.vy * drag + 0.5 * GRIT_G * tt * tt) }
        }
        const a = pos(t), b = pos(Math.max(0, t - 0.018))
        if (a.x < walls.leftWall || a.x > walls.rightWall) continue
        const k = (now - g.born) / g.life
        const alpha = g.a * (1 - k) * (1 - 0.35 * k)
        const c = Math.round(168 + 50 * g.tone)
        ctx.strokeStyle = `rgba(${c - 6},${c - 3},${c + 6},${alpha.toFixed(3)})`
        ctx.lineWidth = g.size * (1 - 0.4 * k)
        ctx.beginPath(); ctx.moveTo(b.x, b.y); ctx.lineTo(a.x + 0.01, a.y); ctx.stroke()
      }
      ctx.restore()
    }
    for (const p of this.pebbles) {
      const age = (now - p.born) / 1000
      const floor = p.floorY - p.r * 0.8
      const tHit = (-p.vy + Math.sqrt(p.vy * p.vy + 2 * PEBBLE_G * Math.max(0, floor - p.y))) / PEBBLE_G
      let x: number, y: number, rot: number
      if (age < tHit) {
        x = p.x + p.vx * age; y = p.y + p.vy * age + 0.5 * PEBBLE_G * age * age; rot = p.rot + p.spin * age
      } else {
        const bvy = -0.25 * (p.vy + PEBBLE_G * tHit)
        const tt = Math.min(age - tHit, (-2 * bvy) / PEBBLE_G)
        x = p.x + p.vx * tHit + p.vx * 0.3 * tt; y = floor + bvy * tt + 0.5 * PEBBLE_G * tt * tt
        rot = p.rot + p.spin * (tHit + 0.4 * tt)
      }
      const k = (now - p.born) / p.life
      x = Math.max(walls.leftWall + p.r, Math.min(walls.rightWall - p.r, x))
      ctx.save(); ctx.translate(x, y); ctx.rotate(rot)
      ctx.globalAlpha = k < 0.7 ? 1 : 1 - (k - 0.7) / 0.3
      ctx.beginPath()
      p.verts.forEach((m, i) => {
        const a = (i / p.verts.length) * Math.PI * 2
        const px = Math.cos(a) * p.r * m, py = Math.sin(a) * p.r * m * 0.8
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py)
      })
      ctx.closePath(); ctx.fillStyle = '#3e3b48'; ctx.fill(); ctx.clip()
      ctx.fillStyle = '#8a8798'; ctx.fillRect(-p.r, -p.r, p.r * 2, p.r * 0.8)
      ctx.restore()
    }
  }
}

export const awokenHopFx = new AwokenHopFx()
